import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ReactNode
} from 'react';
import { createPortal } from 'react-dom';

/**
 * A pop-up menu: the profile menu in the sidebar footer, the workspace and
 * notification menus in the toolbar.
 *
 * The trigger is a render prop and the panel is children, so one component
 * owns both halves and the open state between them. `xPosition` / `yPosition`
 * place the panel relative to the trigger, which is how the profile menu opens
 * upward out of the sidebar footer.
 *
 * The panel carries the `.cw-menu-panel` / `.cw-menu__content` hooks the shell
 * and toolbar stylesheets restyle it through, and it is portalled to `<body>`
 * so the sidebar's `overflow: auto` cannot clip it.
 */

export type MenuXPosition = 'before' | 'after';
export type MenuYPosition = 'above' | 'below';

export interface MenuProps {
  /** Renders the trigger. `open` lets it reflect state, e.g. a chevron flip. */
  trigger: (props: { onClick: (e: React.MouseEvent) => void; open: boolean; ref: React.Ref<never> }) => ReactNode;
  children: ReactNode;
  xPosition?: MenuXPosition;
  yPosition?: MenuYPosition;
  /** Extra classes for the panel. */
  panelClass?: string;
  /** Match the trigger's width - used by the toolbar's search suggestions. */
  matchTriggerWidth?: boolean;
}

const GAP = 4;

export function Menu({
  trigger,
  children,
  xPosition = 'after',
  yPosition = 'below',
  panelClass,
  matchTriggerWidth = false
}: MenuProps) {
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState<React.CSSProperties>({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const close = useCallback(() => setOpen(false), []);

  const position = useCallback(() => {
    const anchor = triggerRef.current;
    const panel = panelRef.current;

    if (!anchor || !panel) {
      return;
    }

    const a = anchor.getBoundingClientRect();
    const p = panel.getBoundingClientRect();

    let top = yPosition === 'below' ? a.bottom + GAP : a.top - p.height - GAP;
    top = Math.max(GAP, Math.min(top, window.innerHeight - p.height - GAP));

    const width = matchTriggerWidth ? { width: a.width, minWidth: a.width } : null;

    // `before` pins the panel's right edge to the trigger's, so it grows back
    // towards the start. Anchoring with `right` rather than `left - width`
    // keeps it aligned even while the open animation (or late-loading fonts)
    // skew the measured width.
    if (xPosition === 'before') {
      const viewport = document.documentElement.clientWidth;
      setStyle({ top, right: Math.max(GAP, viewport - a.right), ...width });
      return;
    }

    const left = Math.max(GAP, Math.min(a.left, window.innerWidth - p.width - GAP));
    setStyle({ top, left, ...width });
  }, [xPosition, yPosition, matchTriggerWidth]);

  useLayoutEffect(() => {
    if (open) {
      position();
    }
  }, [open, position]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;

      if (panelRef.current?.contains(target) || triggerRef.current?.contains(target)) {
        return;
      }

      close();
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close();
        // Send focus back to the trigger that opened the menu.
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', position);
    window.addEventListener('scroll', close, true);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', position);
      window.removeEventListener('scroll', close, true);
    };
  }, [open, close, position]);

  return (
    <>
      {trigger({
        onClick: (event: React.MouseEvent) => {
          event.stopPropagation();
          triggerRef.current = event.currentTarget as HTMLElement;
          setOpen((value) => !value);
        },
        open,
        ref: triggerRef as never
      })}

      {open &&
        createPortal(
          <div className="cw-overlay-pane" style={style}>
            <div
              ref={panelRef}
              className={['cw-menu-panel', panelClass].filter(Boolean).join(' ')}
              role="menu"
              /*
               * A click inside the panel closes it, which is what a menu does -
               * except where a row explicitly stops propagation. The profile
               * menu's header does exactly that so clicking the user's own name
               * is inert rather than dismissing the menu.
               */
              onClick={close}
            >
              <div className="cw-menu__content">{children}</div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

/** One row of a menu - the equivalent of `<button cw-menu-item>`. */
export function MenuItem({
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { children: ReactNode }) {
  return (
    <button
      type="button"
      role="menuitem"
      className={['cw-menu-item', className].filter(Boolean).join(' ')}
      {...rest}
    >
      {children}
    </button>
  );
}

export default Menu;
